import { create } from 'zustand';
import apiClient from '../api/client';
import type { Document, DocumentQueryResult } from '../types';

interface DocumentStore {
  // State
  documents: Document[];
  isLoading: boolean;
  error: string | null;
  uploadProgress: number;
  queryResults: DocumentQueryResult[];
  isQuerying: boolean;

  // Actions
  fetchDocuments: () => Promise<void>;
  uploadDocument: (file: File, chunkingStrategy?: string) => Promise<Document>;
  uploadDocuments: (files: File[], chunkingStrategy?: string) => Promise<Document[]>;
  deleteDocument: (documentId: string) => Promise<void>;
  queryDocuments: (query: string, topK?: number, documentIds?: string[]) => Promise<DocumentQueryResult[]>;
  hybridQueryDocuments: (
    query: string,
    options?: {
      topK?: number;
      documentIds?: string[];
      enableExpansion?: boolean;
      enableReRanking?: boolean;
      fusionMethod?: 'rrf' | 'weighted' | 'max';
      reRankAlgorithm?: 'mmr' | 'relevance' | 'metadata' | 'recency' | 'combined';
    }
  ) => Promise<DocumentQueryResult[]>;
  clearError: () => void;
  clearQueryResults: () => void;
}

export const useDocumentStore = create<DocumentStore>((set) => ({
  // Initial state
  documents: [],
  isLoading: false,
  error: null,
  uploadProgress: 0,
  queryResults: [],
  isQuerying: false,

  // Fetch all documents
  fetchDocuments: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/api/documents');
      const documents = response.data.documents.map((doc: any) => ({
        ...doc,
        createdAt: new Date(doc.createdAt),
      }));
      set({ documents, isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch documents';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // Upload a single document
  uploadDocument: async (file: File, chunkingStrategy?: string) => {
    set({ isLoading: true, error: null, uploadProgress: 0 });

    try {
      // Validate file type - now supports multiple formats
      const supportedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
        'text/plain',
        'text/markdown',
        'text/html',
        'text/csv',
      ];

      if (!supportedTypes.includes(file.type)) {
        throw new Error('Unsupported file type. Supported: PDF, DOCX, TXT, MD, HTML, CSV');
      }

      // Validate file size (50MB limit)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size must be less than 50MB');
      }

      const formData = new FormData();
      formData.append('file', file);
      if (chunkingStrategy) {
        formData.append('chunkingStrategy', chunkingStrategy);
      }

      const response = await apiClient.post('/api/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            set({ uploadProgress: progress });
          }
        },
      });

      const document: Document = {
        ...response.data.document,
        createdAt: new Date(response.data.document.createdAt),
      };

      // Add to documents list
      set((state) => ({
        documents: [document, ...state.documents],
        isLoading: false,
        uploadProgress: 0,
      }));

      return document;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to upload document';
      set({ error: message, isLoading: false, uploadProgress: 0 });
      throw new Error(message);
    }
  },

  // Upload multiple documents in batch
  uploadDocuments: async (files: File[], chunkingStrategy?: string) => {
    set({ isLoading: true, error: null, uploadProgress: 0 });

    try {
      // Validate all files
      const supportedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'text/markdown',
        'text/html',
        'text/csv',
      ];

      const maxSize = 50 * 1024 * 1024;

      for (const file of files) {
        if (!supportedTypes.includes(file.type)) {
          throw new Error(`Unsupported file type: ${file.name}`);
        }
        if (file.size > maxSize) {
          throw new Error(`File too large: ${file.name} (max 50MB)`);
        }
      }

      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      if (chunkingStrategy) {
        formData.append('chunkingStrategy', chunkingStrategy);
      }

      const response = await apiClient.post('/api/documents/batch', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            set({ uploadProgress: progress });
          }
        },
      });

      const documents: Document[] = response.data.documents.map((doc: any) => ({
        ...doc,
        createdAt: new Date(doc.createdAt),
      }));

      // Add to documents list
      set((state) => ({
        documents: [...documents, ...state.documents],
        isLoading: false,
        uploadProgress: 0,
      }));

      return documents;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to upload documents';
      set({ error: message, isLoading: false, uploadProgress: 0 });
      throw new Error(message);
    }
  },

  // Delete a document
  deleteDocument: async (documentId: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.delete(`/api/documents/${documentId}`);

      // Remove from documents list
      set((state) => ({
        documents: state.documents.filter((doc) => doc.id !== documentId),
        isLoading: false,
      }));
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete document';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // Query documents using semantic search
  queryDocuments: async (query: string, topK = 5, documentIds?: string[]) => {
    set({ isQuerying: true, error: null });
    try {
      const response = await apiClient.post('/api/documents/query', {
        query,
        topK,
        documentIds,
      });

      const results: DocumentQueryResult[] = response.data.results || [];
      set({ queryResults: results, isQuerying: false });
      return results;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to query documents';
      set({ error: message, isQuerying: false });
      throw error;
    }
  },

  // Query documents using hybrid search (semantic + keyword)
  hybridQueryDocuments: async (
    query: string,
    options: {
      topK?: number;
      documentIds?: string[];
      enableExpansion?: boolean;
      enableReRanking?: boolean;
      fusionMethod?: 'rrf' | 'weighted' | 'max';
      reRankAlgorithm?: 'mmr' | 'relevance' | 'metadata' | 'recency' | 'combined';
    } = {}
  ) => {
    set({ isQuerying: true, error: null });
    try {
      const response = await apiClient.post('/api/documents/hybrid-query', {
        query,
        topK: options.topK || 5,
        documentIds: options.documentIds,
        enableExpansion: options.enableExpansion !== undefined ? options.enableExpansion : true,
        enableReRanking: options.enableReRanking !== undefined ? options.enableReRanking : true,
        fusionMethod: options.fusionMethod || 'rrf',
        reRankAlgorithm: options.reRankAlgorithm || 'mmr',
      });

      const results: DocumentQueryResult[] = response.data.results || [];
      set({ queryResults: results, isQuerying: false });
      return results;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to query documents';
      set({ error: message, isQuerying: false });
      throw error;
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Clear query results
  clearQueryResults: () => {
    set({ queryResults: [] });
  },
}));
